import moment from 'moment';

import { formatDiscoveryCardDateLine } from './discoveryCardDates';
import { formatDiscoveryCalendarDay } from './eventDateRangeFilter';
import {
  isTicketNetworkEventListing,
  listingEventCalendarDayFromPublicData,
  normalizeListingEventDateString,
} from './listingEventDate';

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DAY_PREFIX = /^(\d{4}-\d{2}-\d{2})/;

const OTHER_PUBLIC_DATE_KEYS = [
  'eventStartDate',
  'event_date',
  'startDate',
  'start_date',
  'date',
  'eventStart',
  'event_start',
];

/**
 * `publicData.eventDateUnix` is stored as UTC start-of-day (a Marketplace API sort index, not
 * a real event instant). Formatting that instant with the viewer's local timezone shifts it
 * back a day for any negative UTC offset — so we read the UTC calendar day from it and render
 * the day in the viewer's local timezone (matches primary-event card formatting).
 */
const dateLineFromEventDateUnix = (unix, intl) => {
  if (unix == null || String(unix).trim() === '') {
    return null;
  }
  const n = Number(unix);
  if (!Number.isFinite(n)) {
    return null;
  }
  const sec = n < 1e12 ? Math.floor(n) : Math.floor(n / 1000);
  const day = moment.unix(sec).utc().format('YYYY-MM-DD');
  return formatDiscoveryCalendarDay(intl, day) || null;
};

/**
 * `publicData.eventDate` is saved as `YYYY-MM-DD`. Render the day via `formatDiscoveryCalendarDay`
 * so it shows the same calendar day regardless of the viewer's timezone. Full ISO strings with
 * a time component fall through to `formatDiscoveryCardDateLine` (local-time parsing).
 */
const dateLineFromEventDateString = (raw, intl) => {
  if (raw == null) {
    return null;
  }
  const s = String(normalizeListingEventDateString(raw)).trim();
  if (!s) {
    return null;
  }
  if (ISO_DAY.test(s)) {
    return formatDiscoveryCalendarDay(intl, s) || null;
  }
  const dayMatch = s.match(ISO_DAY_PREFIX);
  if (dayMatch) {
    const fromDay = formatDiscoveryCalendarDay(intl, dayMatch[1]);
    if (fromDay) {
      return fromDay;
    }
  }
  return formatDiscoveryCardDateLine(intl, s);
};


/**
 * Best-effort event / listing date for Search listing cards (matches primary card formatting).
 */
export const listingCardDateLineFromListing = (listing, intl) => {
  const attrs = listing && listing.attributes;
  if (!attrs) {
    return null;
  }
  const pd = attrs.publicData || {};

  if (isTicketNetworkEventListing(pd)) {
    const tnDay = listingEventCalendarDayFromPublicData(pd);
    if (tnDay) {
      const fromTnDay = dateLineFromEventDateString(tnDay, intl);
      if (fromTnDay) {
        return fromTnDay;
      }
    }
  }

  const fromEventStartDate = dateLineFromEventDateString(pd.eventStartDate, intl);
  if (fromEventStartDate) {
    return fromEventStartDate;
  }

  const fromUnix = dateLineFromEventDateUnix(pd.eventDateUnix, intl);
  if (fromUnix) {
    return fromUnix;
  }

  const fromEventDate = dateLineFromEventDateString(pd.eventDate, intl);
  if (fromEventDate) {
    return fromEventDate;
  }

  for (const key of OTHER_PUBLIC_DATE_KEYS) {
    const v = pd[key];
    if (v != null && String(v).trim()) {
      return formatDiscoveryCardDateLine(intl, v);
    }
  }

  const createdAt = attrs.createdAt;
  if (createdAt != null && String(createdAt).trim()) {
    return formatDiscoveryCardDateLine(intl, createdAt);
  }
  return null;
};
